import {useQuery} from '@apollo/client'
import {Grid2 as Grid, Link, ListItemText} from '@mui/material'
import gql from 'graphql-tag'
import {FC, ReactNode, useCallback, useContext, useMemo, useState} from 'react'
import {useTranslate} from 'react-admin'
import {makeStyles} from 'tss-react/mui'

import ApiUrlsContext from '../../../contexts/ApiUrlsContext'
import {ChatMessageFileAttachments, QueryRoot} from '../../../types/graphqlSchema'
import AudioPlayer from '../ChatToolbar/AudioPlayer'
import FileAttachmentsSwiperDialog from '../FileAttachmentsSwiperDialog'
import ChatMessageTranslation from './ChatMessageTranslation'
import LocationChatMessageFileAttachment from './LocationChatMessageFileAttachment'

const ChatMessageFileAttachment: FC<ChatMessageFileAttachmentProps> = ({
  chatId,
  isTranslationVisible,
  overlay,
  shouldDisplayFileName = true,
  shouldShowImagePreview = true,
  value: a,
}) => {
  const translate = useTranslate()
  const [isDialogOpen, setIsDialogOpen] = useState(false)
  const {getFilesApiUrl} = useContext(ApiUrlsContext)
  const getUrl = useCallback<GetUrl>(({attachment: a}) =>
    a && `${getFilesApiUrl(a.key)}`
  , [getFilesApiUrl])
  const isImageFile = (/image\/\w+/).test(a.mimeType)
  const isAudioFile = (/audio\/\w+/).test(a.mimeType)
  const thumbnailUrl = useMemo<string|undefined>(() => {
    if (isImageFile) return getUrl({attachment: a})
    if (a?.thumbnailMediaKey) return getFilesApiUrl(a.thumbnailMediaKey)
    return undefined
  }, [a, getFilesApiUrl, getUrl, isImageFile])
  const {classes} = useStyles({isAudioFile})
  const mimeTypePattern = `${a.mimeType.split('/')[0]}/%`
  const {
    data: {chat_message_file_attachments: allChatMessageFileAttachments = [a]} = {},
  } = useQuery<QueryRoot['chat_message_file_attachments']>(
    ALL_CHAT_MESSAGE_FILE_ATTACHMENTS_QUERY,
    {
      fetchPolicy: 'cache-and-network',
      skip: !isDialogOpen,
      variables: {chatId, mimeTypePattern},
    }
  )
  // XXX: If there are multiple attachments, they all render hovered at the same time
  return (
    <Grid className={classes.chatMessageFileAttachment} size={{xs: 12}}>
      <>
        {
          (a.mimeType === 'application/location+json') ?
            (<LocationChatMessageFileAttachment value={a.metadata} />) :
            (
              isAudioFile ? (
                <AudioPlayer fileAttachmentUrl={getUrl({attachment: a})} />
              ) : (
                <Link
                  className={`${classes.link} ${classes.attachmentLinkContainer}`}
                  onClick={() => setIsDialogOpen(shouldShowImagePreview)}
                >
                  {!!thumbnailUrl && (
                    <img
                      alt={a.filename}
                      className={classes.thumbnailImage}
                      loading="lazy"
                      src={thumbnailUrl}
                    />
                  )}
                  {overlay}
                  {!isImageFile && shouldDisplayFileName && <div>{a.filename}</div>}
                </Link>
              )
            )
        }
      </>
      {a.transcribedText && (
        <>
          <ListItemText primaryTypographyProps={{variant: 'body2'}}>
            {translate(
              'chat.chatMessageBubble.chatMessageFileAttachmentList.' +
              'chatMessageFileAttachment.transcribedTextWarning',
            )}
          </ListItemText>
          <ListItemText className={classes.transcribedText}>
            {a.transcribedText}
          </ListItemText>
        </>
      )}
      {isTranslationVisible &&
        <ChatMessageTranslation
          translations={a.chatMessageFileAttachmentTranslations}
        />
      }
      <FileAttachmentsSwiperDialog
        chatMessageFileAttachments={allChatMessageFileAttachments}
        value={a}
        isOpen={isDialogOpen}
        onOpen={setIsDialogOpen}
      />
    </Grid>
  )
}

const ALL_CHAT_MESSAGE_FILE_ATTACHMENTS_QUERY = gql`
  query($chatId: uuid!, $mimeTypePattern: String!){
    chat_message_file_attachments(
      order_by: {insertionTimestamp: asc}
      where: {
        mimeType: {_ilike: $mimeTypePattern}
        chatMessage: {_or: [
          {chatId: {_eq: $chatId}}
          {forwardingChatMessages: {chatId: {_eq: $chatId}}}
        ]}
      }
    ){filename height id insertionTimestamp key mimeType width}
  }
`

const useStyles = makeStyles<MakeStylesProps>()((theme, {isAudioFile}) => ({
  attachmentLinkContainer: {
    ...(isAudioFile && {
      '& img': {
        width: '24px',
      },
      alignItems: 'center',
      columnGap: '1rem',
      display: 'flex',
    }),
  },
  chatMessageFileAttachment: {
    padding: '0',
  },
  link: {
    color: theme.palette.info.main,
    cursor: 'pointer',
    fontWeight: 700,
  },
  thumbnailImage: {
    borderRadius: '3px',
    height: '100%',
    objectFit: 'cover',
    width: '100%',
  },
  transcribedText: {
    fontStyle: 'italic',
  },
}))

interface ChatMessageFileAttachmentProps {
  chatId: string
  isTranslationVisible?: boolean
  shouldDisplayFileName?: boolean
  shouldShowImagePreview?: boolean
  value: ChatMessageFileAttachments
  overlay?: ReactNode,
}

interface GetUrl {
  (options: {attachment?: ChatMessageFileAttachments}): string|undefined
}

interface MakeStylesProps {
  isAudioFile?: boolean
}

export default ChatMessageFileAttachment
